import { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import PlayArrowIcon       from '@material-ui/icons/PlayArrow';
import StopIcon            from '@material-ui/icons/Stop';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import { Page, Header, Content, Progress, WarningPanel } from '@backstage/core-components';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';




const FONT = "'Arial', sans-serif";

const MATLAB_EXTENSIONS = ['.m', '.mlx', '.slx'];

const FILE_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  '.m':   { label: 'Script',   color: '#E57200' },
  '.mlx': { label: 'Notebook', color: '#0078D4' },
  '.slx': { label: 'Simulink', color: '#5B9316' },
};

function detectExtension(name: string): string {
  const lower = name.toLowerCase();
  for (const ext of MATLAB_EXTENSIONS) {
    if (lower.endsWith(ext)) return ext;
  }
  return '';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MatlabFile {
  id: string;
  name: string;
  extension: string;
  source: 'persistence' | 'workspace';
  description?: string;
  useCase?: string;
  owner?: string;
  updatedAt?: string;
  workspaceId?: number;
  relativePath?: string;
  workspaceFolder?: string;
  rawRef?: any;
}

type OutputLine =
  | { type: 'stdout';       text: string }
  | { type: 'stderr';       text: string }
  | { type: 'error';        text: string }
  | { type: 'info';         text: string }
  | { type: 'queue_info';   text: string }
  | { type: 'output_saved'; text: string; fileName: string; fileSize: number; isJobLog: boolean };

type JobStatus =
  | 'idle'
  | 'submitting'
  | 'queued'
  | 'running'
  | 'done'
  | 'failed'
  | 'cancelled';

type ActivePrompt = {
  line:      string;
  inputType: 'text' | 'password';
} | null;

// ─── Constants ────────────────────────────────────────────────────────────────

const RESULT_FOLDER_NAME_RE         = /^[a-zA-Z0-9._-]+$/;
const ACTIVE_JOB_STORAGE_KEY_PREFIX = 'matlabRunnerActiveJob::';
const OUTPUT_LINES_STORAGE_PREFIX   = 'matlabRunnerOutputLines::';

// Sentinel text — used to guard the sessionStorage persist effect so we never
// overwrite a good saved state with just the reconnect placeholder line.
const RECONNECT_PLACEHOLDER = 'Reconnecting to your previous MATLAB run...';

type StoredActiveJob = {
  jobId:     string;
  lastSeq:   number;
  updatedAt: number;
};

type ActiveJobProbe = {
  active:    boolean;
  job_id?:   string;
  status?:   'queued' | 'running';
  position?: number | null;
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '24px',
  },
  filterRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    marginBottom: '16px',
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  sourceTabsCard: {
    borderRadius: '8px',
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: '12px',
    background: theme.palette.background.paper,
  },
  sourceTabsHeader: {
    padding: '4px 10px 0 10px',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  sourceTabsBody: {
    padding: '10px 12px 12px 12px',
  },
  fileCard: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    background: theme.palette.background.paper,
    borderRadius: '4px',
    boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
    cursor: 'pointer',
    border: '2px solid transparent',
    transition: 'border-color 0.15s',
    gap: '12px',
    '&:hover': { borderColor: theme.palette.primary.light },
  },
  fileCardSelected: {
    borderColor: theme.palette.primary.main,
  },
  fileIcon: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  fileInfo: {
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    fontFamily: FONT,
    fontWeight: 600,
    fontSize: '14px',
    color: theme.palette.text.primary,
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  fileMeta: {
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: '12px',
    color: theme.palette.text.secondary,
    margin: '2px 0 0 0',
  },
  typeChip: {
    height: '20px',
    fontSize: '11px',
    fontWeight: 700,
    marginLeft: 'auto',
    flexShrink: 0,
  },
  detailPanel: {
    padding: '24px',
    background: theme.palette.background.paper,
    borderRadius: '4px',
    boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    position: 'sticky' as const,
    top: '16px',
  },
  detailLabel: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: theme.palette.text.secondary,
    margin: 0,
  },
  detailValue: {
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: '14px',
    color: theme.palette.text.primary,
    margin: '2px 0 0 0',
    wordBreak: 'break-word' as const,
  },
  executeBtn: {
    marginTop: '8px',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '48px 0',
    color: theme.palette.text.secondary,
  },
  consoleWrapper: {
    marginTop: '24px',
    background: '#1e1e1e',
    borderRadius: '4px',
    padding: '12px 16px',
  },
  consoleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  consoleScroll: {
    overflowY: 'auto' as const,
    maxHeight: '320px',
    fontFamily: "'Consolas', 'Courier New', monospace",
    fontSize: '13px',
    lineHeight: 1.5,
  },
  consoleLine: {
    margin: 0,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const,
  },
  outputFileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '4px 0',
    padding: '4px 8px',
    background: '#2d2d2d',
    borderRadius: '3px',
    border: '1px solid #3c3c3c',
  },
  logFileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '4px 0',
    padding: '4px 8px',
    background: '#1a2a1a',
    borderRadius: '3px',
    border: '1px solid #3a5a3a',
  },
  promptInputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '6px',
    padding: '6px 10px',
    background: '#2d2d2d',
    borderRadius: '4px',
    border: '1px solid #4ec9b0',
  },
  promptInput: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#d4d4d4',
    fontFamily: "'Consolas', 'Courier New', monospace",
    fontSize: '13px',
  },
  promptSendBtn: {
    background: '#4ec9b0',
    border: 'none',
    borderRadius: '3px',
    color: '#1e1e1e',
    padding: '3px 12px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 700,
    flexShrink: 0,
    '&:hover': { background: '#3db89f' },
  },
}));

// ─── sessionStorage helpers ───────────────────────────────────────────────────

function saveOutputLines(storageKey: string, lines: OutputLine[]): void {
  if (!storageKey) return;
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(lines.slice(-500)));
  } catch {
    // sessionStorage full or unavailable — silently ignore
  }
}



function clearOutputLines(storageKey: string): void {
  if (!storageKey) return;
  try { sessionStorage.removeItem(storageKey); } catch { /* ignore */ }
}

// ─── Data hooks ───────────────────────────────────────────────────────────────

function usePersistenceFiles(filterExt: string, filterQ: string) {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi  = useApi(identityApiRef);
  const [files,   setFiles]   = useState<MatlabFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl   = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();
      const params    = new URLSearchParams();
      if (filterQ) params.set('q', filterQ);

      const resp = await fetch(`${baseUrl}/artifacts?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error((await resp.json().catch(() => ({}))).error ?? resp.statusText);

      const data  = await resp.json();
      const all: any[] = data.artifacts ?? [];
      setFiles(
        all
          .filter((a: any) => {
            const ext = detectExtension(a.file_name ?? a.name ?? '');
            return ext !== '' && (filterExt === '' || ext === filterExt);
          })
          .map((a: any): MatlabFile => ({
            id:          `persistence::${a.id ?? a.name}`,
            name:        a.name,
            extension:   detectExtension(a.file_name ?? a.name ?? ''),
            source:      'persistence',
            description: a.description,
            useCase:     a.use_case_tag ?? '',
            owner:       a.uploaded_by ?? '',
            updatedAt:   a.uploaded_at,
            rawRef:      a,
          })),
      );
    } catch (e: any) {
      setError(e.message ?? 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, identityApi, filterExt, filterQ]);

  useEffect(() => { fetch_(); }, [fetch_]);
  return { files, loading, error, refresh: fetch_ };
}

function useWorkspaceFiles(filterExt: string, filterQ: string) {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi  = useApi(identityApiRef);
  const [files,   setFiles]   = useState<MatlabFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl   = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const params    = new URLSearchParams();
      if (filterQ)   params.set('q',   filterQ);
      if (filterExt) params.set('ext', filterExt);

      const resp = await fetch(`${baseUrl}/workspaces/runnable-files?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error((await resp.json().catch(() => ({}))).error ?? resp.statusText);

      const data  = await resp.json();
      const all: any[] = data.files ?? [];
      setFiles(
        all
          .map((f: any): MatlabFile => ({
            id:              `workspace::${f.workspace_id}::${f.relative_path}`,
            name:            f.name,
            extension:       detectExtension(f.name ?? ''),
            source:          'workspace',
            owner:           f.owner_username ?? '',
            updatedAt:       f.updated_at,
            workspaceId:     Number(f.workspace_id),
            relativePath:    f.relative_path,
            workspaceFolder: f.folder_name,
            rawRef:          f,
          }))
          .filter((f: MatlabFile) => f.extension !== ''),
      );
    } catch (e: any) {
      setError(e.message ?? 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, identityApi, filterExt, filterQ]);

  useEffect(() => { fetch_(); }, [fetch_]);
  return { files, loading, error, refresh: fetch_ };
}

// ─── Detail panel ─────────────────────────────────────────────────────────────

const LINE_COLORS: Record<string, string> = {
  stdout:       '#d4d4d4',
  stderr:       '#ffcc00',
  error:        '#f44747',
  info:         '#4ec9b0',
  queue_info:   '#b0a0ff',
  output_saved: '#89d185',
};

type DetailPanelProps = {
  file:          MatlabFile | null;
  jobStatus:     JobStatus;
  queuePosition: number | null;
  onExecute:     (f: MatlabFile) => void;
  onStop:        () => void;
};

const DetailPanel = ({ file, jobStatus, queuePosition, onExecute, onStop }: DetailPanelProps) => {
  const classes = useStyles();

  if (!file) {
    return (
      <Paper className={classes.detailPanel} elevation={0}>
        <Typography variant="body2" color="textSecondary"
          style={{ textAlign: 'center', paddingTop: '32px' }}>
          Select a file to see details
        </Typography>
      </Paper>
    );
  }

  const typeInfo = FILE_TYPE_LABELS[file.extension] ?? { label: file.extension, color: '#888' };
  const isActive = jobStatus === 'running' || jobStatus === 'queued' || jobStatus === 'submitting';

  return (
    <Paper className={classes.detailPanel} elevation={0}>
      <div>
        <p className={classes.detailLabel}>File</p>
        <p className={classes.detailValue} style={{ fontWeight: 600, fontSize: '16px' }}>
          {file.name}
        </p>
      </div>

      <div>
        <p className={classes.detailLabel}>Type</p>
        <Chip label={typeInfo.label} size="small"
          style={{ background: typeInfo.color, color: '#fff', fontWeight: 700, marginTop: '4px' }} />
      </div>

      {file.description && (
        <div>
          <p className={classes.detailLabel}>Description</p>
          <p className={classes.detailValue}>{file.description}</p>
        </div>
      )}
      {file.owner && (
        <div>
          <p className={classes.detailLabel}>Owner</p>
          <p className={classes.detailValue}>{file.owner}</p>
        </div>
      )}
      {file.useCase && (
        <div>
          <p className={classes.detailLabel}>Use Case</p>
          <p className={classes.detailValue}>{file.useCase}</p>
        </div>
      )}
      {file.updatedAt && (
        <div>
          <p className={classes.detailLabel}>Last Updated</p>
          <p className={classes.detailValue}>{new Date(file.updatedAt).toLocaleDateString()}</p>
        </div>
      )}

      <Divider />

      {isActive ? (
        <>
          <Button variant="outlined" color="secondary" fullWidth
            startIcon={<StopIcon />} className={classes.executeBtn} onClick={onStop}>
            Stop Execution
          </Button>
          <Typography variant="caption" color="textSecondary"
            style={{ textAlign: 'center', display: 'flex', alignItems: 'center',
                     justifyContent: 'center', gap: 6 }}>
            <CircularProgress size={12} />
            {jobStatus === 'submitting'   ? 'Submitting…'
              : jobStatus === 'queued'
              ? queuePosition === 1 ? 'Next in queue…' : `Queued — position ${queuePosition}`
              : 'Running in MATLAB…'}
          </Typography>
        </>
      ) : (
        <Button variant="contained" color="primary" fullWidth
          startIcon={<PlayArrowIcon />} className={classes.executeBtn}
          onClick={() => onExecute(file)}>
          {jobStatus === 'done'      ? 'Run Again'
            : jobStatus === 'failed'    ? 'Retry'
            : jobStatus === 'cancelled' ? 'Run Again'
            : 'Execute'}
        </Button>
      )}

      {jobStatus === 'done' && (
        <Typography variant="caption" style={{ color: '#89d185', textAlign: 'center' }}>
          Last run completed successfully
        </Typography>
      )}
      {jobStatus === 'failed' && (
        <Typography variant="caption" style={{ color: '#f44747', textAlign: 'center' }}>
          Last run failed - check console output
        </Typography>
      )}
      {jobStatus === 'cancelled' && (
        <Typography variant="caption" style={{ color: '#ffcc00', textAlign: 'center' }}>
          Last run was cancelled
        </Typography>
      )}
    </Paper>
  );
};

// ─── Console output panel ─────────────────────────────────────────────────────

type ConsoleOutputProps = {
  lines:         OutputLine[];
  jobStatus:     JobStatus;
  queuePosition: number | null;
  onClear:       () => void;
  activePrompt:  ActivePrompt;
  inputValue:    string;
  onInputChange: (value: string) => void;
  onSendInput:   (inputType: 'text' | 'password') => void;
};

const ConsoleOutput = ({
  lines,
  jobStatus,
  queuePosition,
  onClear,
  activePrompt,
  inputValue,
  onInputChange,
  onSendInput,
}: ConsoleOutputProps) => {
  const classes        = useStyles();
  const scrollRef      = useRef<HTMLDivElement>(null);
  const promptInputRef = useRef<HTMLInputElement>(null);
  const canSendInput   = Boolean(activePrompt);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [lines, activePrompt]);

  useEffect(() => {
    if (canSendInput && promptInputRef.current) {
      promptInputRef.current.focus();
    }
  }, [canSendInput]);

  const isActive = jobStatus === 'running' || jobStatus === 'queued' || jobStatus === 'submitting';

  if (lines.length === 0 && !isActive) return null;

  return (
    <Paper className={classes.consoleWrapper} elevation={0}>

      {/* ── Header ── */}
      <div className={classes.consoleHeader}>
        <Typography variant="caption"
          style={{ color: '#858585', fontFamily: 'monospace',
                   textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Console Output
        </Typography>
        <Button size="small" onClick={onClear}
          style={{ color: '#858585', minWidth: 0, padding: '0 8px', fontSize: '12px' }}>
          Clear
        </Button>
      </div>

      {/* ── Scrollable area ── */}
      <div ref={scrollRef} className={classes.consoleScroll}>

        {/* Placeholder when nothing has arrived yet */}
        {isActive && lines.length === 0 && (
          <p className={classes.consoleLine} style={{ color: '#858585' }}>
            {jobStatus === 'submitting'   ? 'Submitting job…'
              : queuePosition !== null
              ? queuePosition === 1
                ? 'Waiting to start — next in queue…'
                : `Waiting in queue (position ${queuePosition})…`
              : 'Waiting for output…'}
          </p>
        )}

        {/* Output lines */}
        {lines.map((l, i) => {
          if (l.type === 'output_saved') {
            // ── Execution log file row — distinct styling, no interactive element ──
            if (l.isJobLog) {
              return (
                <div key={i} className={classes.logFileRow}>
                  <span style={{ fontSize: '13px' }}>📋</span>
                  <span style={{ color: '#89d185', fontSize: '12px', flex: 1 }}>
                    {l.fileName}
                  </span>
                  <span style={{ color: '#858585', fontSize: '11px' }}>
                    {formatBytes(l.fileSize)}
                  </span>
                  <span style={{
                    color: '#5a9a5a',
                    fontSize: '11px',
                    fontStyle: 'italic',
                    marginLeft: '4px',
                  }}>
                    execution log — saved automatically
                  </span>
                </div>
              );
            }

            // ── Regular output file row ────────────────────────────────────────
            return (
              <div key={i} className={classes.outputFileRow}>
                <span style={{ color: '#89d185', fontSize: '12px', flex: 1 }}>
                  {l.fileName}
                  <span style={{ color: '#858585', marginLeft: '6px' }}>
                    ({formatBytes(l.fileSize)})
                  </span>
                </span>
                <span style={{ color: '#858585', fontSize: '11px' }}>
                  saved to workspace
                </span>
              </div>
            );
          }

          // ── Plain text line ────────────────────────────────────────────────
          return (
            <p key={i} className={classes.consoleLine}
              style={{ color: LINE_COLORS[l.type] ?? '#d4d4d4' }}>
              {l.text}
            </p>
          );
        })}

        {/* Blinking cursor while running */}
        {jobStatus === 'running' && !activePrompt && (
          <p className={classes.consoleLine} style={{ color: '#d4d4d4' }}>
            <span style={{ animation: 'blink 1s step-end infinite' }}>█</span>
          </p>
        )}

        {/* Stdin input box */}
        {canSendInput && (
          <div className={classes.promptInputRow}>
            <span style={{ color: '#4ec9b0', fontSize: '13px', fontFamily: 'monospace',
                           flexShrink: 0, userSelect: 'none' }}>
              {'>'}
            </span>
            <input
              ref={promptInputRef}
              type={activePrompt?.inputType ?? 'text'}
              value={inputValue}
              onChange={e => onInputChange(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') onSendInput(activePrompt?.inputType ?? 'text');
              }}
              placeholder={
                activePrompt?.inputType === 'password'
                  ? 'Enter password and press Enter…'
                  : 'Type terminal input and press Enter…'
              }
              className={classes.promptInput}
              autoComplete={activePrompt?.inputType === 'password' ? 'current-password' : 'off'}
            />
            <button className={classes.promptSendBtn}
              onClick={() => onSendInput(activePrompt?.inputType ?? 'text')}>
              Send
            </button>
          </div>
        )}
      </div>

      {/* ── Status bar ── */}
      <div style={{ marginTop: '8px', paddingTop: '6px', borderTop: '1px solid #333',
                    display: 'flex', alignItems: 'center', gap: '6px' }}>
        {isActive && <CircularProgress size={10} style={{ color: '#4ec9b0' }} />}
        {canSendInput && (
          <Typography variant="caption" style={{ color: '#4ec9b0', fontFamily: 'monospace' }}>
            Terminal input enabled
          </Typography>
        )}
        {!activePrompt && (
          <Typography variant="caption" style={{
            color: jobStatus === 'done'      ? '#89d185'
                 : jobStatus === 'failed'    ? '#f44747'
                 : jobStatus === 'cancelled' ? '#ffcc00'
                 : '#858585',
            fontFamily: 'monospace',
          }}>
            {jobStatus === 'submitting'   ? 'Submitting…'
              : jobStatus === 'queued'    ? `Queued - position ${queuePosition ?? '?'}`
              : jobStatus === 'running'   ? 'Running…'
              : jobStatus === 'done'      ? 'Execution complete'
              : jobStatus === 'failed'    ? 'Execution failed'
              : jobStatus === 'cancelled' ? 'Cancelled'
              : ''}
          </Typography>
        )}
      </div>
    </Paper>
  );
};

// ─── File list ────────────────────────────────────────────────────────────────

type FileListProps = {
  files:    MatlabFile[];
  selected: MatlabFile | null;
  onSelect: (f: MatlabFile) => void;
};

const FileListPanel = ({ files, selected, onSelect }: FileListProps) => {
  const classes = useStyles();
  if (files.length === 0) {
    return (
      <div className={classes.emptyState}>
        <InsertDriveFileIcon style={{ fontSize: 48, opacity: 0.3 }} />
        <Typography variant="body2" style={{ marginTop: 8 }}>
          No MATLAB files found for the selected filters.
        </Typography>
      </div>
    );
  }
  return (
    <div className={classes.fileList}>
      {files.map(f => {
        const typeInfo   = FILE_TYPE_LABELS[f.extension] ?? { label: f.extension, color: '#888' };
        const isSelected = selected?.id === f.id;
        return (
          <div
            key={f.id}
            className={`${classes.fileCard} ${isSelected ? classes.fileCardSelected : ''}`}
            onClick={() => onSelect(f)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && onSelect(f)}
          >
            <InsertDriveFileIcon className={classes.fileIcon} />
            <div className={classes.fileInfo}>
              <p className={classes.fileName}>{f.name}</p>
              <p className={classes.fileMeta}>
                {[
                  f.source === 'workspace'
                    ? `Workspace: ${f.workspaceFolder ?? 'unknown'}`
                    : 'Artifact',
                  f.owner,
                  f.useCase,
                  f.updatedAt ? new Date(f.updatedAt).toLocaleDateString() : '',
                ].filter(Boolean).join(' · ')}
              </p>
            </div>
            <Chip label={typeInfo.label} size="small" className={classes.typeChip}
              style={{ background: typeInfo.color, color: '#fff' }} />
          </div>
        );
      })}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const MatlabPage = () => {
  const classes      = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi  = useApi(identityApiRef);

  // ── Filters ───────────────────────────────────────────────────────────────
  const [filterExt,  setFilterExt]  = useState('');
  const [filterQ,    setFilterQ]    = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filterQ), 300);
    return () => clearTimeout(t);
  }, [filterQ]);

  const persistence = usePersistenceFiles(filterExt, debouncedQ);
  const workspace   = useWorkspaceFiles(filterExt, debouncedQ);

  const [activeSourceTab, setActiveSourceTab] = useState<'artifacts' | 'workspace'>('artifacts');

  const visibleFiles       = activeSourceTab === 'artifacts' ? persistence.files : workspace.files;
  const sortedVisibleFiles = [...visibleFiles].sort((a, b) => {
    const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return tb - ta;
  });

  const loading = persistence.loading || workspace.loading;
  const error   = persistence.error ?? workspace.error;

  // ── Selection ─────────────────────────────────────────────────────────────
  const [selectedFile, setSelectedFile] = useState<MatlabFile | null>(null);

  useEffect(() => {
    if (!selectedFile) return;
    if (activeSourceTab === 'artifacts' && selectedFile.source !== 'persistence') setSelectedFile(null);
    if (activeSourceTab === 'workspace' && selectedFile.source !== 'workspace')   setSelectedFile(null);
  }, [activeSourceTab, selectedFile]);

  // ── Job state ─────────────────────────────────────────────────────────────
  const [jobStatus,     setJobStatus]     = useState<JobStatus>('idle');
  const [currentJobId,  setCurrentJobId]  = useState<string | null>(null);
  const [queuePosition, setQueuePosition] = useState<number | null>(null);
  const [outputLines,   setOutputLines]   = useState<OutputLine[]>([]);
  const [userEntityRef, setUserEntityRef] = useState<string>('');

  // ── Stdin state ───────────────────────────────────────────────────────────
  const [activePrompt, setActivePrompt] = useState<ActivePrompt>(null);
  const [inputValue,   setInputValue]   = useState('');

  // ── Execute dialog state ──────────────────────────────────────────────────
  const [executeDialogOpen,  setExecuteDialogOpen]  = useState(false);
  const [executeTargetFile,  setExecuteTargetFile]  = useState<MatlabFile | null>(null);
  const [resultFolderInput,  setResultFolderInput]  = useState('');
  const [executeDialogError, setExecuteDialogError] = useState('');

  const abortControllerRef    = useRef<AbortController | null>(null);
  const currentSeqRef         = useRef<number>(0);
  const reconnectAttemptedRef = useRef<boolean>(false);
  const currentJobIdRef       = useRef<string | null>(null);
  const hadToWaitRef          = useRef<boolean>(false);
  useEffect(() => { currentJobIdRef.current = currentJobId; }, [currentJobId]);

  // ── Identity ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        if (mounted) setUserEntityRef(identity.userEntityRef ?? '');
      } catch {
        if (mounted) setUserEntityRef('');
      }
    })();
    return () => { mounted = false; };
  }, [identityApi]);

  // ── Storage keys ──────────────────────────────────────────────────────────
  const activeJobStorageKey = userEntityRef
    ? `${ACTIVE_JOB_STORAGE_KEY_PREFIX}${userEntityRef}`
    : '';

  const outputLinesStorageKey = currentJobId
    ? `${OUTPUT_LINES_STORAGE_PREFIX}${currentJobId}`
    : '';

  // ── Persist output lines to sessionStorage on every change ────────────────
  // Guard: do NOT overwrite a good saved state with just the reconnect
  // placeholder. The SSE replay will fill in the real lines immediately after.
  useEffect(() => {
    if (!outputLinesStorageKey) return;
    if (
      outputLines.length === 1 &&
      outputLines[0].type === 'info' &&
      outputLines[0].text === RECONNECT_PLACEHOLDER
    ) return;
    saveOutputLines(outputLinesStorageKey, outputLines);
  }, [outputLines, outputLinesStorageKey]);

  // ── Reset reconnect guard when identity resolves ──────────────────────────
  useEffect(() => {
    reconnectAttemptedRef.current = false;
  }, [activeJobStorageKey]);

  // ── localStorage helpers ──────────────────────────────────────────────────
  const saveActiveJobState = useCallback((jobId: string, lastSeq: number) => {
    if (!activeJobStorageKey || !jobId) return;
    const payload: StoredActiveJob = { jobId, lastSeq, updatedAt: Date.now() };
    localStorage.setItem(activeJobStorageKey, JSON.stringify(payload));
  }, [activeJobStorageKey]);

  const clearActiveJobState = useCallback(() => {
    if (!activeJobStorageKey) return;
    localStorage.removeItem(activeJobStorageKey);
  }, [activeJobStorageKey]);

  // ── SSE event handler ─────────────────────────────────────────────────────
  const handleRunnerEvent = useCallback((eventType: string, payload: any) => {
    const seq = Number(payload?.seq);
    if (Number.isFinite(seq) && seq > currentSeqRef.current) {
      currentSeqRef.current = seq;
      const knownJobId = payload.job_id ?? currentJobIdRef.current;
      if (knownJobId) saveActiveJobState(knownJobId, currentSeqRef.current);
    }

    switch (eventType) {
      case 'queued': {
        const nextJobId = payload.job_id ?? null;
        setJobStatus('queued');
        setCurrentJobId(nextJobId);
        currentJobIdRef.current = nextJobId;
        setQueuePosition(payload.position ?? null);
        if (nextJobId) saveActiveJobState(nextJobId, currentSeqRef.current);

        // Only show queue message and set hadToWait if user actually has to wait
        if (payload.needs_queue_message) {
          hadToWaitRef.current = true;
          setOutputLines(prev => [
            ...prev.filter(l => l.type !== 'queue_info'),
            {
              type: 'queue_info' as const,
              text: `Job queued. You are number ${payload.position ?? '?'} in the queue.`,
            },
          ]);
        }
        break;
      }

      case 'queue_update': {
        setQueuePosition(payload.position ?? null);

        // Only show updates to users who actually had to wait
        if (hadToWaitRef.current) {
          setOutputLines(prev => [
            ...prev.filter(l => l.type !== 'queue_info'),
            {
              type: 'queue_info' as const,
              text: payload.position === 1
                ? 'Queue update: you are now number 1. Your run will start next.'
                : `Queue update: your position is now ${payload.position ?? '?'}.`,
            },
          ]);
        }
        break;
      }

      case 'started': {
        setJobStatus('running');
        const nextJobId = payload.job_id ?? currentJobIdRef.current;
        if (nextJobId) {
          setCurrentJobId(nextJobId);
          currentJobIdRef.current = nextJobId;
          saveActiveJobState(nextJobId, currentSeqRef.current);
        }
        setQueuePosition(null);
        hadToWaitRef.current = false;  // 👈 reset — no longer waiting

        setOutputLines(prev => [
          ...prev.filter(l => l.type !== 'queue_info'),  // 👈 remove queue line cleanly
          { type: 'info' as const, text: 'MATLAB job started.' },
        ]);
        break;
      }

      case 'stdout':
        setOutputLines(prev => [...prev, {
          type: 'stdout' as const,
          text: payload.line ?? payload.text ?? '',
        }]);
        break;

      case 'stderr':
        setOutputLines(prev => [...prev, {
          type: 'stderr' as const,
          text: payload.line ?? payload.text ?? '',
        }]);
        break;

      case 'status':
        // heartbeat-style — no UI update needed
        break;

      case 'input_prompt':
        setActivePrompt({
          line:      payload.text ?? payload.line ?? '',
          inputType: payload.inputType === 'password' ? 'password' : 'text',
        });
        break;

      case 'output_saved':
        setOutputLines(prev => [...prev, {
          type:     'output_saved' as const,
          text:     payload.file_name ?? '',
          fileName: payload.file_name ?? '',
          fileSize: payload.file_size ?? 0,
          isJobLog: Boolean(payload.is_job_log),
        }]);
        break;

      case 'error':
        setOutputLines(prev => [...prev, {
          type: 'error' as const,
          text: payload.message ?? payload.error ?? 'Unknown error',
        }]);
        break;

      case 'cancelled':
        setJobStatus('cancelled');
        setActivePrompt(null);
        setOutputLines(prev => [...prev, {
          type: 'error' as const,
          text: payload.message ?? 'Job cancelled.',
        }]);
        break;

      case 'done': {
        const exitCode = payload.exit_code ?? payload.exitCode;
        setJobStatus(exitCode === 0 ? 'done' : 'failed');
        setQueuePosition(null);
        setActivePrompt(null);
        setInputValue('');
        setOutputLines(prev => [...prev, {
          type: exitCode === 0 ? 'info' as const : 'error' as const,
          text: exitCode === 0
            ? `Execution completed successfully (exit code: ${exitCode}).`
            : `MATLAB exited with code ${exitCode}.`,
        }]);
        break;
      }

      default:
        break;
    }
  }, [saveActiveJobState]);

  const parseSseBlock = useCallback((block: string) => {
    if (!block.trim()) return;
    let eventName = 'message';
    let dataStr   = '';
    for (const rawLine of block.split(/\r?\n/)) {
      if (rawLine.startsWith('event:'))     eventName = rawLine.slice(6).trim();
      else if (rawLine.startsWith('data:')) dataStr   = rawLine.slice(5).trim();
    }
    if (!dataStr) return;
    let payload: any = {};
    try { payload = JSON.parse(dataStr); } catch { return; }
    const eventType = eventName !== 'message' ? eventName : (payload.type ?? 'message');
    handleRunnerEvent(eventType, payload);
  }, [handleRunnerEvent]);

  const defaultResultFolderForFile = useCallback((file: MatlabFile) => {
    if (file.relativePath?.includes('/')) {
      const parts  = file.relativePath.split('/').filter(Boolean);
      const parent = parts.length > 1 ? parts[parts.length - 2] : '';
      if (parent) return `${parent}_results`;
    }
    const dot  = file.name.lastIndexOf('.');
    const base = dot > 0 ? file.name.slice(0, dot) : file.name;
    return `${base}_results`;
  }, []);

  // ── Send stdin input ──────────────────────────────────────────────────────
  const handleSendInput = useCallback(async (inputType: 'text' | 'password') => {
    const jobId = currentJobIdRef.current;
    if (!jobId) {
      setOutputLines(prev => [...prev, {
        type: 'error' as const,
        text: 'Cannot send input: no active MATLAB job id.',
      }]);
      return;
    }
    const textToSend = inputValue;
    if (!textToSend.trim()) return;

    try {
      const baseUrl   = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();

      const resp = await fetch(`${baseUrl}/artifacts/input/${jobId}`, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text: textToSend, inputType }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.error ?? `Input submission failed (${resp.status})`);
      }

      setInputValue('');
      setActivePrompt(null);
    } catch {
      setOutputLines(prev => [...prev, {
        type: 'error' as const,
        text: 'Failed to send input to MATLAB. Please try again.',
      }]);
    }
  }, [inputValue, discoveryApi, identityApi]);

  // ── Execute ───────────────────────────────────────────────────────────────
  const executeFile = useCallback(async (file: MatlabFile, resultFolderName: string) => {
    const rawId = file.rawRef?.id;
    if (file.source === 'persistence' && !rawId) return;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setJobStatus('submitting');
    setCurrentJobId(null);
    currentJobIdRef.current = null;
    setQueuePosition(null);
    setOutputLines([]);
    setActivePrompt(null);
    setInputValue('');
    currentSeqRef.current = 0;
    clearActiveJobState();
    reconnectAttemptedRef.current = true;
    hadToWaitRef.current = false;

    try {
      const baseUrl   = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();

      const executePath =
        file.source === 'workspace'
          ? `${baseUrl}/artifacts/workspace/execute`
          : `${baseUrl}/artifacts/${rawId}/execute`;

      const requestBody =
        file.source === 'workspace'
          ? {
              source:             'workspace',
              workspace_id:       file.workspaceId,
              workspace_owner:    file.rawRef?.owner_username,
              workspace_folder:   file.rawRef?.folder_name,
              relative_path:      file.relativePath,
              result_folder_name: resultFolderName,
            }
          : {
              source:             'persistence',
              result_folder_name: resultFolderName,
            };

      const resp = await fetch(executePath, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body:   JSON.stringify(requestBody),
        signal: controller.signal,
      });

      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        setJobStatus('failed');
        setOutputLines([{ type: 'error', text: errBody.error ?? resp.statusText }]);
        return;
      }

      const reader = resp.body?.getReader();
      if (!reader) {
        setJobStatus('failed');
        setOutputLines([{ type: 'error', text: 'No response body received from server.' }]);
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split(/\r?\n\r?\n/);
        buffer = blocks.pop() ?? '';
        for (const block of blocks) parseSseBlock(block);
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        setJobStatus('failed');
        setOutputLines(prev => [...prev, {
          type: 'error' as const,
          text: err?.message ?? 'Unknown error',
        }]);
      }
    } finally {
      abortControllerRef.current = null;
    }
  }, [clearActiveJobState, discoveryApi, identityApi, parseSseBlock]);

  // ── Reconnect to existing job ─────────────────────────────────────────────
  // The SSE history replay is the single source of truth for output lines.
  // We do NOT rehydrate from sessionStorage here — doing so AND replaying
  // causes every line to appear twice.
  // sessionStorage is only used to persist lines so they survive the brief
  // moment between navigation-away and the SSE stream reconnecting.
  const reconnectToExistingJob = useCallback(async (jobId: string, sinceSeq: number) => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setCurrentJobId(jobId);
    currentJobIdRef.current = jobId;
    currentSeqRef.current   = sinceSeq;

    // Show a single placeholder line while the stream reconnects.
    // The persist effect will NOT save this (guarded by RECONNECT_PLACEHOLDER check).
    setOutputLines([{ type: 'info' as const, text: RECONNECT_PLACEHOLDER }]);

    try {
      const baseUrl   = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();
      let requestedSinceSeq = Math.max(0, sinceSeq);

      // Probe current job status so we can set the right UI state before
      // the stream arrives.
      try {
        const posResp = await fetch(`${baseUrl}/artifacts/queue-position/${jobId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal:  controller.signal,
        });
        if (posResp.ok) {
          const body = await posResp.json().catch(() => ({}));
          if      (body.status === 'queued')    { setJobStatus('queued');  setQueuePosition(body.position ?? null); }
          else if (body.status === 'running')   { setJobStatus('running'); setQueuePosition(null); }
          else if (body.status === 'completed') { setJobStatus('done');    setQueuePosition(null); requestedSinceSeq = 0; }
        }
      } catch { /* best effort */ }

      const readStream = async (seq: number): Promise<number> => {
        const streamResp = await fetch(
          `${baseUrl}/artifacts/stream/${jobId}?since_seq=${Math.max(0, seq)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {}, signal: controller.signal },
        );

        if (!streamResp.ok) {
          const errBody = await streamResp.json().catch(() => ({}));
          if (streamResp.status === 404 || streamResp.status === 403) {
            clearActiveJobState();
            setCurrentJobId(null);
            currentJobIdRef.current = null;
            setJobStatus('idle');
          }
          setOutputLines(prev => [...prev, {
            type: 'error' as const,
            text: errBody.error ?? `Reconnect failed (${streamResp.status})`,
          }]);
          return -1;
        }

        const reader = streamResp.body?.getReader();
        if (!reader) {
          setOutputLines(prev => [...prev, {
            type: 'error' as const,
            text: 'Reconnect stream did not return a readable body.',
          }]);
          return -1;
        }

        const decoder  = new TextDecoder();
        let buffer     = '';
        let blockCount = 0;

        // The first event from the replay will call setOutputLines via
        // handleRunnerEvent, which replaces the placeholder with real content
        // because each event appends to prev (and prev starts as the placeholder).
        // We clear the placeholder on the very first real event below.
        let clearedPlaceholder = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const blocks = buffer.split(/\r?\n\r?\n/);
          buffer = blocks.pop() ?? '';
          for (const block of blocks) {
            if (!block.trim()) continue;
            // On the first real SSE block, wipe the placeholder so the
            // replayed history starts clean.
            if (!clearedPlaceholder) {
              clearedPlaceholder = true;
              setOutputLines([]);
            }
            blockCount++;
            parseSseBlock(block);
          }
        }
        return blockCount;
      };

      const primaryBlocks = await readStream(requestedSinceSeq);
      // If we got nothing back with since_seq > 0, retry from the beginning
      // (the server may have restarted and lost partial history).
      if (primaryBlocks === 0 && requestedSinceSeq > 0) await readStream(0);

    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        setOutputLines(prev => [...prev, {
          type: 'error' as const,
          text: err?.message ?? 'Failed to reconnect to running job.',
        }]);
      }
    } finally {
      abortControllerRef.current = null;
    }
  }, [clearActiveJobState, discoveryApi, identityApi, parseSseBlock]);

  const findServerActiveJob = useCallback(async (): Promise<ActiveJobProbe | null> => {
    try {
      const baseUrl   = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(`${baseUrl}/artifacts/queue/my-active`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) return null;
      return (await resp.json().catch(() => null)) as ActiveJobProbe | null;
    } catch {
      return null;
    }
  }, [discoveryApi, identityApi]);

  // ── On mount: reconnect to any active job ─────────────────────────────────
  useEffect(() => {
    if (!activeJobStorageKey || reconnectAttemptedRef.current) return;
    reconnectAttemptedRef.current = true;

    const raw = localStorage.getItem(activeJobStorageKey);
    if (raw) {
      try {
        const stored = JSON.parse(raw) as StoredActiveJob;
        if (stored?.jobId) {
          reconnectToExistingJob(stored.jobId, Number(stored.lastSeq ?? 0));
          return;
        }
      } catch {
        clearActiveJobState();
      }
    }

    (async () => {
      const probe = await findServerActiveJob();
      if (!probe?.active || !probe.job_id) return;
      const seq = 0;
      saveActiveJobState(probe.job_id, seq);
      if      (probe.status === 'queued')  { setJobStatus('queued');  setQueuePosition(probe.position ?? null); }
      else if (probe.status === 'running') { setJobStatus('running'); setQueuePosition(null); }
      await reconnectToExistingJob(probe.job_id, seq);
    })();
  }, [
    activeJobStorageKey,
    clearActiveJobState,
    findServerActiveJob,
    reconnectToExistingJob,
    saveActiveJobState,
  ]);

  const handleExecute = useCallback((file: MatlabFile) => {
    setExecuteTargetFile(file);
    setResultFolderInput(defaultResultFolderForFile(file));
    setExecuteDialogError('');
    setExecuteDialogOpen(true);
  }, [defaultResultFolderForFile]);

  const handleConfirmExecute = useCallback(async () => {
    if (!executeTargetFile) return;

    const trimmed = resultFolderInput.trim();
    if (trimmed && !RESULT_FOLDER_NAME_RE.test(trimmed)) {
      setExecuteDialogError('Use only letters, numbers, dot, underscore, or hyphen.');
      return;
    }

    setExecuteDialogOpen(false);

    // Clear any previous session log before starting a fresh run so stale
    // lines from a previous job never bleed into the new one.
    if (outputLinesStorageKey) clearOutputLines(outputLinesStorageKey);

    // Check whether there is already an active job for this user before
    // starting a new one.
    const storedRaw = activeJobStorageKey ? localStorage.getItem(activeJobStorageKey) : null;
    if (storedRaw) {
      try {
        const stored = JSON.parse(storedRaw) as StoredActiveJob;
        if (stored?.jobId) {
          const baseUrl   = await discoveryApi.getBaseUrl('persistence');
          const { token } = await identityApi.getCredentials();
          const posResp   = await fetch(`${baseUrl}/artifacts/queue-position/${stored.jobId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (posResp.ok) {
            const posBody = await posResp.json().catch(() => ({}));
            const status  = String(posBody?.status ?? '');
            if (status === 'queued' || status === 'running') {
              setOutputLines(prev => [...prev, {
                type: 'info' as const,
                text: 'You already have an active MATLAB run. Reconnecting instead of starting a new one.',
              }]);
              await reconnectToExistingJob(stored.jobId, Number(stored.lastSeq ?? 0));
              return;
            }
          }
        }
      } catch { /* fallback to normal execute */ }
    }

    const probe = await findServerActiveJob();
    if (probe?.active && probe.job_id) {
      setOutputLines(prev => [...prev, {
        type: 'info' as const,
        text: `An active MATLAB run is already ${probe.status}. Reconnecting now.`,
      }]);
      await reconnectToExistingJob(probe.job_id, 0);
      return;
    }

    await executeFile(executeTargetFile, trimmed);
  }, [
    activeJobStorageKey,
    discoveryApi,
    executeTargetFile,
    executeFile,
    findServerActiveJob,
    identityApi,
    outputLinesStorageKey,
    reconnectToExistingJob,
    resultFolderInput,
  ]);

  // ── Stop ──────────────────────────────────────────────────────────────────
  const handleStop = useCallback(async () => {
    const jobId = currentJobIdRef.current;
    if (jobId) {
      try {
        const baseUrl   = await discoveryApi.getBaseUrl('persistence');
        const { token } = await identityApi.getCredentials();
        const identity  = await identityApi.getBackstageIdentity();
        await fetch(`${baseUrl}/artifacts/cancel/${jobId}`, {
          method:  'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ user_ref: identity.userEntityRef, is_admin: false }),
        });
      } catch { /* best-effort */ }
    }
    abortControllerRef.current?.abort();
    setJobStatus('cancelled');
    setQueuePosition(null);
    setActivePrompt(null);
    setInputValue('');
    clearActiveJobState();
    setOutputLines(prev => [...prev, {
      type: 'error' as const,
      text: 'Execution stopped by user.',
    }]);
  }, [clearActiveJobState, discoveryApi, identityApi]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Page themeId="tool">
      <Header
        title="Matlab Runner"
        subtitle="Select a MATLAB script, live notebook, or Simulink model and run it"
      />
      <Content>
        <Grid container spacing={3}>

          {/* Left: filters + file list */}
          <Grid item xs={12} md={8}>
            <div className={classes.filterRow}>
              <TextField
                label="Search files" variant="outlined" size="small"
                style={{ flex: '1 1 220px', minWidth: 180 }}
                value={filterQ} onChange={e => setFilterQ(e.target.value)}
                placeholder="Name, description…"
              />
              <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
                <InputLabel>File Type</InputLabel>
                <Select value={filterExt}
                  onChange={e => setFilterExt(e.target.value as string)} label="File Type">
                  <MenuItem value="">All types</MenuItem>
                  <MenuItem value=".m">Script (.m)</MenuItem>
                  <MenuItem value=".mlx">Live Notebook (.mlx)</MenuItem>
                  <MenuItem value=".slx">Simulink (.slx)</MenuItem>
                </Select>
              </FormControl>
              {sortedVisibleFiles.length > 0 && (
                <Typography variant="caption" color="textSecondary" style={{ marginLeft: 'auto' }}>
                  {sortedVisibleFiles.length} file{sortedVisibleFiles.length !== 1 ? 's' : ''} found
                </Typography>
              )}
            </div>

            <Paper elevation={0} className={classes.sourceTabsCard}>
              <div className={classes.sourceTabsHeader}>
                <Tabs value={activeSourceTab} onChange={(_e, v) => setActiveSourceTab(v)}
                  indicatorColor="primary" textColor="primary" variant="fullWidth">
                  <Tab value="artifacts"  label={`Artifacts (${persistence.files.length})`} />
                  <Tab value="workspace" label={`MATLAB Workspace (${workspace.files.length})`} />
                </Tabs>
              </div>
              <div className={classes.sourceTabsBody}>
                <Typography variant="caption" color="textSecondary">
                  {activeSourceTab === 'artifacts'
                    ? 'Files uploaded in Persistence Repository'
                    : 'Runnable files discovered from your accessible MATLAB workspaces'}
                </Typography>
              </div>
            </Paper>

            {loading && <Progress />}
            {!loading && error && <WarningPanel title="Failed to load files" message={error} />}
            {!loading && !error && (
              <FileListPanel files={sortedVisibleFiles} selected={selectedFile}
                onSelect={setSelectedFile} />
            )}
            {!loading && !error && sortedVisibleFiles.length === 0
              && filterExt === '' && filterQ === '' && (
              <Typography variant="caption" color="textSecondary"
                style={{ display: 'block', marginTop: 8 }}>
                {activeSourceTab === 'artifacts'
                  ? 'Tip: Upload MATLAB files (.m, .mlx, .slx) to the Persistence Repository to see them here.'
                  : 'No runnable workspace files found yet. Create or open a MATLAB workspace and add .m/.mlx/.slx files.'}
              </Typography>
            )}
          </Grid>

          {/* Right: detail panel */}
          <Grid item xs={12} md={4}>
            <DetailPanel
              file={selectedFile}
              jobStatus={jobStatus}
              queuePosition={queuePosition}
              onExecute={handleExecute}
              onStop={handleStop}
            />
          </Grid>
        </Grid>

        {/* Console output — full width below the grid */}
        <ConsoleOutput
          lines={outputLines}
          jobStatus={jobStatus}
          queuePosition={queuePosition}
          onClear={() => {
            setOutputLines([]);
            setActivePrompt(null);
            setInputValue('');
            if (outputLinesStorageKey) clearOutputLines(outputLinesStorageKey);
            if (
              jobStatus !== 'running' &&
              jobStatus !== 'queued' &&
              jobStatus !== 'submitting'
            ) {
              setJobStatus('idle');
              clearActiveJobState();
              setCurrentJobId(null);
              currentJobIdRef.current = null;
              currentSeqRef.current   = 0;
            }
          }}
          activePrompt={activePrompt}
          inputValue={inputValue}
          onInputChange={setInputValue}
          onSendInput={handleSendInput}
        />

        {/* Execute confirmation dialog */}
        <Dialog open={executeDialogOpen} onClose={() => setExecuteDialogOpen(false)}
          maxWidth="sm" fullWidth>
          <DialogTitle>Execution Output Location</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
              {executeTargetFile?.source === 'workspace'
                ? 'Results from MATLAB Workspace files stay inside the same workspace storage.'
                : 'Results from Artifact files are saved in the Persistence repository tree.'}
            </Typography>
            <Typography variant="body2" style={{ marginBottom: 12 }}>
              Selected file: <strong>{executeTargetFile?.name ?? '—'}</strong>
            </Typography>
            <TextField
              label="Result Folder Name" variant="outlined" size="small" fullWidth
              value={resultFolderInput}
              onChange={e => { setResultFolderInput(e.target.value); setExecuteDialogError(''); }}
              placeholder="e.g. model_results"
              helperText="Leave empty to save in the same folder. Allowed: letters, numbers, dot, underscore, hyphen."
              error={!!executeDialogError}
            />
            {executeDialogError && (
              <Typography variant="caption" color="error">{executeDialogError}</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExecuteDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={handleConfirmExecute}>
              Start Execution
            </Button>
          </DialogActions>
        </Dialog>
      </Content>

      <style>{`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0; }
        }
      `}</style>
    </Page>
  );
};